import { useQuery } from "@tanstack/react-query";
import { getSupabase, isSupabaseConfigured } from "../lib/supabase";

export interface WarTrack {
  /** Storage filename, e.g. "02-rage.mp3" — used as a stable key. */
  filename: string;
  /** Human-readable label derived from the filename. */
  displayName: string;
  /** Public URL streamable directly by an <audio> element. */
  url: string;
}

/**
 * Lists every track in the public `war_music` Storage bucket.
 *
 * `folder` selects a per-theme playlist:
 *   ""          → bucket root  (norse / BERSERKR — legacy location)
 *   "frontier"  → frontier/    (LAW DOGG — drop western mp3s here)
 *
 * Sorted by filename ascending; prefix uploads with 01-, 02-, … for order.
 * 5-minute staleTime: tracks don't change often during a session.
 */
export function useWarMusic(folder = "") {
  return useQuery<WarTrack[], Error>({
    queryKey: ["war-music", folder],
    enabled: isSupabaseConfigured(),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const supabase = getSupabase();
      if (!supabase) return [];

      const { data, error } = await supabase.storage
        .from("war_music")
        .list(folder, { limit: 100, sortBy: { column: "name", order: "asc" } });

      if (error) throw new Error(error.message);
      if (!data) return [];

      return data
        .filter((obj) => /\.(mp3|wav|ogg)$/i.test(obj.name))
        .map((obj) => {
          const path = folder ? `${folder}/${obj.name}` : obj.name;
          const { data: pub } = supabase.storage
            .from("war_music")
            .getPublicUrl(path);
          return {
            filename: obj.name,
            displayName: filenameToDisplay(obj.name),
            url: pub.publicUrl,
          };
        });
    },
  });
}

/**
 * "02-berserker-rage.mp3" → "Berserker Rage"
 */
function filenameToDisplay(filename: string): string {
  const noExt = filename.replace(/\.[^/.]+$/, "");
  const stripped = noExt.replace(/^\d+\s*[-_]?\s*/, "");
  const spaced = stripped.replace(/[-_]+/g, " ").trim();
  return spaced
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
